import { create } from 'zustand'
import {
  NODE_BRANCHES,
  NODE_SPECS,
  freeCellFrom,
  isKnownNodeType,
  isLoop,
  nodeRole,
  positionForChild,
  withNodeDefaults,
  type FlowGraph,
  type FlowNode,
  type NodeBranch,
  type NodePosition,
  type NodeType,
} from '@/features/flows/types.ts'
import { mintNodeId, mintNodeKey } from './mint.ts'

/**
 * The staged graph — every edit a person has made and not yet saved (D9).
 *
 * **It is UI state, not a second copy of server state.** The saved graph stays
 * in SWR under `/pipelines/:pid/flows/:fid`, which is where the console's rule
 * puts it (`apps/console/CLAUDE.md` § State boundaries); what lives here is the
 * buffer between the last thing the engine acknowledged and the next `PUT`.
 * That is why {@link FlowEditorState.reset} takes the graph to fall back to
 * rather than keeping one: a store that held both would be holding server state
 * and would have to be kept coherent with SWR by hand.
 *
 * **Seeding happens once per flow, deliberately.** {@link FlowEditorState.open}
 * is a no-op while the store is already on that flow, because SWR revalidates
 * on focus and on reconnect — and a reseed on revalidation would delete
 * whatever the person had drawn since. The two paths that *do* reseed are
 * explicit acts: `reset` (Discard) and the 409 banner's Reload.
 *
 * Every mutator writes a new `nodes` array and new node objects. The draft is
 * read by `useSyncExternalStore` underneath zustand, so an in-place edit would
 * change what a render reads without telling React it had.
 */

/** A refusal the canvas has to say out loud, cleared on the next gesture. */
export type EditorNotice = 'loop' | 'trigger_undeletable' | 'role_capped'

/** One end of a connection a keyboard user has armed but not yet completed. */
export interface PendingConnection {
  source: string
  branch: NodeBranch
}

interface FlowEditorState {
  /** Which flow the draft belongs to. `null` before the first {@link open}. */
  flowId: string | null
  draft: FlowGraph | null
  /**
   * The `flows.version` the draft was **seeded from** — the value the save's
   * compare-and-swap has to carry.
   *
   * It is held here rather than read off the SWR row at save time because the
   * two drift: `open` deliberately ignores revalidations so edits survive, so a
   * background refetch can advance the cached version while the draft still
   * describes the graph before it. Sending that newer version would make the
   * engine's CAS *match* and overwrite the other writer's save with no 409 —
   * the one outcome the compare-and-swap exists to prevent.
   */
  baseVersion: number | null
  /** Set by every mutator, cleared by `open`, `reset` and `markSaved`. */
  dirty: boolean
  selectedNodeId: string | null
  pending: PendingConnection | null
  notice: EditorNotice | null

  open: (flowId: string, graph: FlowGraph, version: number) => void
  reset: (flowId: string, graph: FlowGraph, version: number) => void
  markSaved: (flowId: string, graph: FlowGraph, version: number) => void
  /** Drop the staged graph, so the next {@link open} seeds instead of no-opping. */
  close: () => void

  select: (nodeId: string | null) => void
  moveNode: (nodeId: string, position: NodePosition) => void
  /** Replace one node's whole options object — the drawer's only write. */
  setNodeOptions: (nodeId: string, options: Record<string, unknown>) => void
  addNode: (type: NodeType) => void
  deleteNode: (nodeId: string) => void
  duplicateNode: (nodeId: string) => void
  connect: (source: string, branch: NodeBranch, target: string) => void
  disconnect: (source: string, branch: NodeBranch) => void

  arm: (source: string, branch: NodeBranch) => void
  disarm: () => void
  clearNotice: () => void
}

/** Where an unattached new node lands: below the selection, else below the graph. */
function placeNewNode(graph: FlowGraph, selectedNodeId: string | null): NodePosition {
  const anchor = graph.nodes.find((n) => n.id === selectedNodeId)
  if (anchor) return positionForChild(anchor, 'resolve', graph)
  if (graph.nodes.length === 0) return { x: 1, y: 1 }
  const lowest = Math.max(...graph.nodes.map((n) => n.position.y))
  return freeCellFrom(graph, { x: 1, y: lowest + 1 })
}

export const useFlowEditorState = create<FlowEditorState>((set, get) => {
  /** Every structural edit goes through here, so `dirty` cannot be forgotten. */
  const edit = (fn: (draft: FlowGraph) => FlowGraph | null): void => {
    const { draft } = get()
    if (draft === null) return
    const next = fn(draft)
    if (next === null) return
    set({ draft: next, dirty: true })
  }

  const mapNodes = (draft: FlowGraph, fn: (node: FlowNode) => FlowNode): FlowGraph => ({
    ...draft,
    nodes: draft.nodes.map(fn),
  })

  return {
    flowId: null,
    draft: null,
    baseVersion: null,
    dirty: false,
    selectedNodeId: null,
    pending: null,
    notice: null,

    open: (flowId, graph, version) => {
      if (get().flowId === flowId) return
      set({
        flowId,
        draft: graph,
        baseVersion: version,
        dirty: false,
        selectedNodeId: null,
        pending: null,
        notice: null,
      })
    },

    reset: (flowId, graph, version) =>
      set({
        flowId,
        draft: graph,
        baseVersion: version,
        dirty: false,
        selectedNodeId: null,
        pending: null,
        notice: null,
      }),

    /**
     * Record what the engine acknowledged, **without discarding edits made
     * while the save was in flight**.
     *
     * The canvas stays interactive during a save, so by the time the PUT
     * resolves the store may already hold a newer draft than the one that was
     * sent. Writing `graph` back unconditionally would silently revert that
     * edit and clear `dirty` on top of it, leaving the change lost and
     * unsaveable. So the draft is replaced only while it is still the value
     * that was sent; otherwise the newer draft stands and stays dirty.
     *
     * `baseVersion` advances either way — the save happened, and the next one
     * must compare against the version it produced.
     *
     * **Scoped to the flow that was saved.** The store is a module singleton,
     * so a save started on flow A can resolve after the route has moved to
     * flow B. Without the `flowId` guard, A's acknowledgement wrote its version
     * onto B's `baseVersion`, and B's next save then cited a version it was
     * never built on — a compare-and-swap that fails, and a 409 shown to
     * someone who had no conflict. An acknowledgement for a flow that is no
     * longer open is simply dropped.
     */
    markSaved: (flowId, graph, version) =>
      set((state) => {
        if (state.flowId !== flowId) return {}
        return state.draft === graph
          ? { draft: graph, baseVersion: version, dirty: false }
          : { baseVersion: version }
      }),

    close: () =>
      set({
        flowId: null,
        draft: null,
        baseVersion: null,
        dirty: false,
        selectedNodeId: null,
        pending: null,
        notice: null,
      }),

    select: (selectedNodeId) => set({ selectedNodeId, notice: null }),

    moveNode: (nodeId, position) =>
      edit((draft) => mapNodes(draft, (n) => (n.id === nodeId ? { ...n, position } : n))),

    /**
     * The drawer writes the node's options **whole**, not field by field.
     *
     * A per-field mutator would have to know which fields a type has, which is
     * the per-type knowledge `NodeSpec.fields` exists to keep out of the
     * console. The form owns the object while it is open and hands back the
     * result; `validateFlowGraph` on the draft then marks whatever is wrong,
     * exactly as it does for every other edit.
     */
    setNodeOptions: (nodeId, options) =>
      edit((draft) => mapNodes(draft, (n) => (n.id === nodeId ? { ...n, options } : n))),

    addNode: (type) =>
      edit((draft) => {
        const node: FlowNode = {
          id: mintNodeId(draft),
          key: mintNodeKey(draft, type),
          type,
          // The spec's declared defaults, filled at birth rather than left
          // absent: an option a person never opened still has to mean what
          // `NodeSpec.parse` will read, and the overview row has to show it.
          options: withNodeDefaults(type, {}),
          resolve: null,
          reject: null,
          position: placeNewNode(draft, get().selectedNodeId),
        }
        set({ selectedNodeId: node.id })
        return { ...draft, nodes: [...draft.nodes, node] }
      }),

    /**
     * Delete a node, re-point its parent, and retire its key.
     *
     * The parent inherits the deleted node's `resolve` — Directus's behaviour
     * (study §8) — so removing a step from the middle of a chain leaves the
     * chain joined instead of severed. Its `reject` child is *not* inherited:
     * a branch has one target, and choosing which of two children survives is
     * a decision the canvas has no basis for making.
     *
     * The key is retired because a run step that cited it must never resolve to
     * a future node minted with the same name (`FlowGraph.retiredKeys`).
     */
    deleteNode: (nodeId) =>
      edit((draft) => {
        const victim = draft.nodes.find((n) => n.id === nodeId)
        if (victim === undefined) return null
        if (nodeRole(victim.type) === 'trigger') {
          set({ notice: 'trigger_undeletable' })
          return null
        }

        const nodes = draft.nodes
          .filter((n) => n.id !== nodeId)
          .map((n) => ({
            ...n,
            resolve: n.resolve === nodeId ? victim.resolve : n.resolve,
            reject: n.reject === nodeId ? victim.resolve : n.reject,
          }))

        const retiredKeys = [...(draft.retiredKeys ?? [])]
        if (victim.key !== '' && !retiredKeys.includes(victim.key)) retiredKeys.push(victim.key)

        set({ selectedNodeId: null, pending: null })
        // Spread, like every sibling mutator: enumerating the two fields it
        // touches would silently drop a third the day `FlowGraph` grows one.
        return { ...draft, nodes, retiredKeys }
      }),

    /**
     * A copy of one node's options, as a new node with no edges.
     *
     * The edges are dropped rather than copied because a branch has exactly one
     * target and at most one parent: a duplicate that kept its original's
     * pointers would make both `multiple_parents` and a second holder of the
     * same child, so the copy would arrive unsaveable.
     */
    duplicateNode: (nodeId) =>
      edit((draft) => {
        const original = draft.nodes.find((n) => n.id === nodeId)
        if (original === undefined) return null

        // The same cap the Add palette enforces, applied here too. Without it
        // Duplicate is a second door onto a second trigger: the graph then
        // fails `multiple_triggers`, `entryNodeOf` returns null because there
        // is no single entry, and `unreachableNodeIds` greys out every node —
        // an unsaveable canvas reached by a button that looked available.
        const role = nodeRole(original.type)
        if (role === 'trigger' || role === 'action') {
          set({ notice: 'role_capped' })
          return null
        }

        const copy: FlowNode = {
          id: mintNodeId(draft),
          key: mintNodeKey(draft, original.type),
          type: original.type,
          options: structuredClone(original.options),
          resolve: null,
          reject: null,
          position: freeCellFrom(draft, {
            x: original.position.x,
            y: original.position.y + 1,
          }),
        }
        set({ selectedNodeId: copy.id })
        return { ...draft, nodes: [...draft.nodes, copy] }
      }),

    /**
     * Point `source`'s `branch` at `target`, unless that closes a loop.
     *
     * The loop is the one refusal the canvas makes at the drop rather than at
     * the save (D9): a cyclic graph cannot be stored, so accepting the edge
     * would hand back a canvas that has to be repaired before it can be saved
     * at all. Every other rule — a second parent, a successor after an action —
     * is *marked* instead, because those are states a person passes through on
     * the way to a graph they mean.
     */
    connect: (source, branch, target) =>
      edit((draft) => {
        if (isLoop(draft, source, target)) {
          set({ notice: 'loop', pending: null })
          return null
        }
        set({ pending: null, notice: null })
        return mapNodes(draft, (n) => (n.id === source ? { ...n, [branch]: target } : n))
      }),

    disconnect: (source, branch) =>
      edit((draft) => mapNodes(draft, (n) => (n.id === source ? { ...n, [branch]: null } : n))),

    arm: (source, branch) => {
      const pending = get().pending
      const same = pending?.source === source && pending.branch === branch
      set({ pending: same ? null : { source, branch }, notice: null })
    },
    disarm: () => set({ pending: null }),
    clearNotice: () => set({ notice: null }),
  }
})

/**
 * Which of a node's two exits and its entrance the canvas offers, read off the
 * node's role rather than its spelling.
 *
 * It is the validator's role rules (`packages/core/src/flows/validate.ts` §3),
 * expressed as handles: nothing may point at a trigger and a trigger has no
 * reject (`trigger_is_targeted`, `trigger_has_reject`), and nothing may follow
 * an action (`action_has_successor`). A handle that cannot legally be used is
 * not drawn, so those three refusals are unreachable from the canvas instead of
 * being explained after a failed save.
 */
export function handlesOf(type: string): { target: boolean; sources: readonly NodeBranch[] } {
  // A type this build has no spec for gets every handle rather than none. The
  // node is already drawn (the API layer refuses to blank an editor over one),
  // and withholding handles would drop the very edges the validator flags —
  // leaving a red-ringed node with no arrow to select and no way to repair it
  // from the canvas that reported it.
  if (!isKnownNodeType(type)) return { target: true, sources: NODE_BRANCHES }
  const role = nodeRole(type)
  if (role === 'trigger') return { target: false, sources: ['resolve'] }
  if (role === 'action') return { target: true, sources: [] }
  return { target: true, sources: NODE_BRANCHES }
}

/**
 * The overview rows a node's own spec produces for the card body (D9).
 *
 * Empty for a type this build has no spec for. `lib/api/flows.ts` reads a
 * node's `type` as a plain string on purpose — refusing an unknown one "would
 * blank an editor the operator opened to fix exactly that" — and indexing
 * `NODE_SPECS` with it anyway threw, which blanked the editor regardless.
 */
export const overviewOf = (node: FlowNode) =>
  isKnownNodeType(node.type) ? NODE_SPECS[node.type].overview(node.options) : []
